import { chain, identity } from 'lodash';
import pako from 'pako';

import { request } from '@/utils/infrastructure/communication';
import { toQuery } from '@/utils/infrastructure/utils';
import RequestTracker from '@/utils/infrastructure/communication/requestTracker';

// Entity identifiers that should be preserved in requests
const ENTITY_ID_FIELDS = [
  'taskListId',
  'versionId',
  'formInstanceId',
  'processGroupId',
  'processInstanceId',
  'actionInstanceId',
  'listId',
  'processId',
  'formId',
  'entityId',
  'token',
];

/**
 * HttpService - Simplified HTTP transport service with inline request management
 * Handles HTTP transport concerns with built-in deduplication and policy enforcement
 */
class HttpService {
  constructor() {
    this.activeRequests = new Map();
  }

  async request(url, options = {}) {
    let trackingInfo = null;

    try {
      if (!url || typeof url !== 'string') {
        throw new Error('Request URL is required and must be a string');
      }

      if (url.includes('[object Object]')) {
        throw new Error('Request cancelled - invalid URL format');
      }

      const transformedConfig = this._transformParameters(url, options);
      const finalConfig = this._applyPolicies(transformedConfig);
      this._manageRequestDeduplication(finalConfig.url, finalConfig.controller);

      // Register with RequestTracker if namespace provided
      const namespace = options.namespace;
      if (namespace) {
        const tracker = RequestTracker.getInstance();
        if (tracker.isEnabled(namespace)) {
          const requestId = tracker.register(namespace, finalConfig.controller, finalConfig.url);
          if (requestId) {
            trackingInfo = { namespace, requestId };
          }
        }
      }

      return await this._executeRequest(finalConfig.url, finalConfig.options);
    } catch (error) {
      // Unregister on error
      if (trackingInfo) {
        RequestTracker.getInstance().unregister(trackingInfo.namespace, trackingInfo.requestId);
      }
      this._cleanupRequest(url);
      throw error;
    } finally {
      // Unregister on completion
      if (trackingInfo) {
        RequestTracker.getInstance().unregister(trackingInfo.namespace, trackingInfo.requestId);
      }
      this._cleanupRequest(url);
    }
  }

  cancelRequest(url) {
    if (!this.activeRequests.has(url)) return false;

    const requestInfo = this.activeRequests.get(url);
    if (requestInfo?.controller) requestInfo.controller.abort();

    this.activeRequests.delete(url);
    return true;
  }

  getActiveRequestCount() {
    return this.activeRequests.size;
  }

  isRequestActive(url) {
    return this.activeRequests.has(url);
  }

  _transformParameters(url, options = {}) {
    const {
      method = 'get',
      searchQuery,
      body,
      data,
      headers = {},
      params = {},
      namespace,
      ...more
    } = options;

    // Validate query parameters only (exclude system options)
    const systemOptions = [
      'method',
      'searchQuery',
      'body',
      'data',
      'headers',
      'params',
      'readPriority',
      'gzip',
      'signal',
      'timeout',
      'namespace',
    ];

    const queryParams = Object.keys(more).reduce((acc, key) => {
      if (!systemOptions.includes(key)) {
        acc[key] = more[key];
      }
      return acc;
    }, {});

    const allParams = { ...params, ...queryParams };
    const filteredParams = Object.fromEntries(
      Object.entries(allParams).filter(([, value]) => value !== undefined),
    );

    const hasInvalidParams = Object.values(filteredParams).some(
      (value) => typeof value === 'string' && value.includes('[object Object]'),
    );

    if (hasInvalidParams) {
      throw new Error('Invalid parameters detected in request');
    }

    const transformedHeaders = {
      ...headers,
      ...(method === 'post' && { 'Content-Type': 'application/json' }),
      ...(more.readPriority && { 'AUTOMY-Read-Priority': 'primary' }),
      ...(more.gzip && { 'Content-Encoding': 'gzip' }),
    };

    const transformedParams = {
      ...toQuery({ ...params, ...more }),
      ...chain(params).pick(ENTITY_ID_FIELDS).pickBy(identity).value(),
      ...(searchQuery && { searchQuery }),
      ...(params.searchQuery && { searchQuery: params.searchQuery }),
    };

    let transformedData = body || data;
    const controller = new AbortController();

    if (more.gzip) {
      transformedData = pako.gzip(JSON.stringify(transformedData));
    }

    return {
      url,
      controller,
      options: {
        method,
        headers: transformedHeaders,
        params: transformedParams,
        data: transformedData,
        signal: controller.signal,
        ...more,
      },
    };
  }

  _applyPolicies(requestConfig) {
    const modifiedConfig = { ...requestConfig };
    const { options } = modifiedConfig;
    const method = options.method?.toLowerCase();

    // Policy 1: Content-Type for POST requests (priority 10)
    if (method === 'post') {
      modifiedConfig.options.headers = {
        'Content-Type': 'application/json',
        ...modifiedConfig.options.headers,
      };
    }

    // Policy 2: Read priority header (priority 5)
    if (options.readPriority === true) {
      modifiedConfig.options.headers = {
        'AUTOMY-Read-Priority': 'primary',
        ...modifiedConfig.options.headers,
      };
    }

    // Policy 3: Read priority header (priority 5)
    if (options.gzip === true) {
      modifiedConfig.options.headers = {
        'Content-Encoding': 'gzip',
        ...modifiedConfig.options.headers,
      };
    }

    // Policy 3: Request timeout (priority 3)
    if (!options.timeout) {
      const defaultTimeout = method === 'post' || method === 'put' ? 300_000 : 120_000;
      modifiedConfig.options.timeout = defaultTimeout;
    }

    return modifiedConfig;
  }

  _manageRequestDeduplication(url, controller) {
    const hadExistingRequest = this.cancelRequest(url);
    this.activeRequests.set(url, { url, controller });
    return hadExistingRequest;
  }

  // Clean up request tracking (without aborting - request already completed or errored)
  _cleanupRequest(url) {
    if (this.activeRequests.has(url)) {
      this.activeRequests.delete(url);
    }
  }

  async _executeRequest(url, options) {
    return await request(url, options);
  }
}

let instance;

HttpService.getInstance = () => {
  if (!instance) instance = new HttpService();
  return instance;
};

export default HttpService;
